// ============================================================================
// GROUPING WRAPPER - PURE GROUPED-ITEM HELPERS
// ============================================================================
//
// Split out of `useGroupingState.ts`: these are pure functions over an already
// grouped item list (no React, no state). Keeping them beside the hook made one
// module answerable for both "how grouping state evolves" and "how a grouped
// list is read", which is two reasons to change.

import type { GroupedItem } from "@/lib/utils/crm/grouping-strategies";

export function collectPrimaryGroupKeys(groupedData: GroupedItem[]): Set<string> {
  const keys = new Set<string>();
  groupedData.forEach((item) => {
    if (item.type === "group-header" && item.groupKey) {
      keys.add(item.groupKey);
    }
  });
  return keys;
}

export function collectNestedExpandedGroups(groupedData: GroupedItem[]): Map<string, Set<string>> {
  const nested = new Map<string, Set<string>>();
  groupedData.forEach((item) => {
    if (item.type === "group-header" && item.groupKeys && item.groupKeys.length === 2) {
      const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
      if (!nested.has(primaryKey)) {
        nested.set(primaryKey, new Set());
      }
      nested.get(primaryKey)!.add(secondaryKey);
    }
  });
  return nested;
}

export function filterGroupedItems(
  groupedData: GroupedItem[],
  expandedGroups: Set<string>,
  nestedExpandedGroups: Map<string, Set<string>>
): GroupedItem[] {
  return groupedData.filter((item) => {
    if (item.type === "group-header") {
      return true;
    }

    if (item.type === "data") {
      if (item.groupKeys && item.groupKeys.length === 2) {
        const [primaryKey, secondaryKey] = item.groupKeys as [string, string];
        const secondaryGroupsForPrimary = nestedExpandedGroups.get(primaryKey);
        const primaryExpanded =
          secondaryGroupsForPrimary !== undefined && secondaryGroupsForPrimary.size > 0;
        return primaryExpanded && secondaryGroupsForPrimary?.has(secondaryKey);
      }

      if (item.groupKey !== undefined && item.groupKey !== null) {
        return expandedGroups.has(item.groupKey);
      }
    }

    return true;
  });
}
